/*
 * Copyright (c) 2026 Oray Inc. All rights reserved.
 *
 * No Part of this file may be reproduced, stored
 * in a retrieval system, or transmitted, in any form, or by any means,
 * electronic, mechanical, photocopying, recording, or otherwise,
 * without the prior consent of Oray Inc.
 *
 * @Author: huangweiliang<huangweiliang@oray.com>
 * @Date: 2026-01-13
 * @LastEditors: huangweiliang<huangweiliang@oray.com>
 * @LastEditTime: 2026-01-13 18:42:40
 * @FileName: strategies.ts
 * @Description: []
 */

import { BridgeHandler, BridgeConfig } from './types'
import { Env } from './env'

/**
 * 辅助函数：根据配置处理 payload
 */
function getPayload(data: any, config?: BridgeConfig): any {
  if (config?.payloadType === 'object') {
    return typeof data === 'string' ? JSON.parse(data || '{}') : data || {}
  }
  // default string
  return typeof data === 'object' ? JSON.stringify(data) : data || ''
}

/**
 * 通信策略集合
 */
export const BridgeStrategies = {
  // Flutter 容器
  flutter: (action: string, data?: any, config?: BridgeConfig) => {
    // 模式 A: Sunlogin Flutter (WebChannel)
    if (config?.flutterMode === 'webchannel') {
      const payload = getPayload(data, { ...config, payloadType: 'object' }) // SunFlutter 似乎接收对象
      if (window.messageHandlers?.WebChannel) {
        window.messageHandlers.WebChannel.postMessage({
          event: action,
          data: payload,
        })
      } else {
        console.warn('[OraySDK ClientWeb] window.messageHandlers.WebChannel not found')
      }
      return
    }

    // 模式 B: Standard Flutter (InAppWebView)
    if (!window.flutter_inappwebview) return

    const payload = getPayload(data, config) // 默认 string

    // 针对旧版本
    if (Env.clientType === 'pgy-game' && Env.appVersion === '7.0.0') {
      const legacyMethods = ['jsMethod', 'gameBoosterOpen', 'gameBoosterClose', 'getGameBoosterState']
      if (!legacyMethods.includes(action)) {
        window.flutter_inappwebview.callHandler(action, payload)
        return
      }
    }

    window.flutter_inappwebview.callHandler('jsMethod', {
      method: action,
      data: payload,
    })
  },

  // iOS / Mac (WKWebView ScriptMessageHandler)
  webkit: (action: string, data?: any, config?: BridgeConfig) => {
    const payload = getPayload(data, config)
    const handlers = window.webkit?.messageHandlers

    // 1. 尝试标准 WKWebView MessageHandlers
    if (handlers) {
      // 1.1 指定 Handler 名称
      if (config?.webkitHandlerName && handlers[config.webkitHandlerName]) {
        handlers[config.webkitHandlerName].postMessage({
          method: action,
          data: payload,
        })
        return
      }

      // 1.2 Action 即 Handler 名称
      if (handlers[action]) {
        handlers[action].postMessage(payload)
        return
      }

      // 1.3 兼容 WebChannel (向日葵 Flutter 在某些环境下的表现)
      if (handlers.WebChannel) {
        handlers.WebChannel.postMessage({
          event: action,
          data: typeof payload === 'string' ? JSON.parse(payload) : payload,
        })
        return
      }
    }

    // 2. 兜底：尝试 window.messageHandlers (Legacy or Custom Injection)
    if (window.messageHandlers && typeof window.messageHandlers[action] === 'function') {
      window.messageHandlers[action](payload)
      return
    }

    console.warn(`[OraySDK ClientWeb] iOS/Mac Handler not found: ${config?.webkitHandlerName || action}`)
  },

  // Android / Harmony / Windows (注入对象映射)
  injectedObject: (action: string, data?: any, config?: BridgeConfig) => {
    const payload = getPayload(data, config)

    // 1. Harmony WebChannel 特殊处理
    if (config?.harmonyMode === 'webchannel') {
      if ((window as any).WebChannel) {
        ;(window as any).WebChannel.postMessage(payload)
        return
      }
    }

    const targets = config?.injectObjectNames || ['messageHandlers', 'androidInterface']

    // 2. 遍历配置的注入对象名
    for (const name of targets) {
      const obj = window[name]
      if (obj && typeof obj[action] === 'function') {
        obj[action](payload)
        return
      }
    }

    // 3. 兼容 Linux 或其他特殊注入
    if (window.messagehandler) {
      window.messagehandler(action, payload)
      return
    }

    // 4. 尝试直接挂载在 window 上的方法 (Windows/Other)
    if (typeof window[action] === 'function') {
      window[action](payload)
      return
    }

    console.warn(`[OraySDK ClientWeb] Native Object Handler not found for action: ${action}. Checked: ${targets.join(', ')}`)
  },
}

/**
 * 工厂函数：根据当前环境获取最佳策略
 */
export function createPlatformHandler(): BridgeHandler {
  if (Env.isFlutter) {
    return BridgeStrategies.flutter
  }

  if (Env.platform.isMac || Env.platform.isIos || Env.platform.isIpad) {
    return BridgeStrategies.webkit
  }

  // Android, Harmony, Windows, Linux
  return BridgeStrategies.injectedObject
}
